import { useState, useEffect, useMemo } from 'react';
import { Form, Button, Spinner, Row, Col, Card, InputGroup, Alert } from 'react-bootstrap';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useServicesQuery, useUsersMutation } from '../../../../../api/user/user.hooks';
import { useEticketingCitiesQuery, useEticketingUsersRolesQuery } from '../../../../../api/e-ticketing/e-ticketing.hooks';

const ETicketingUserForm = ({
  onCancel,
  onSave,
  user = null,
  mode = "create", // 'create', 'edit', 'view'
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    module: "e-ticketing",
    roles: "",
    Service: "",
    service_id: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const { create: createCloudUser, update: updateCloudUser, loading: loadingUser, error: errorUser } = useUsersMutation();
  const { services, loading: loadingServices, error: errorServices } = useServicesQuery();
  const { data: cities, isLoading: loadingCities, error: errorCities } = useEticketingCitiesQuery()

  const { data: usersRoles, isLoading: loadingUsersRoles, error: errorUsersRoles } = useEticketingUsersRolesQuery();

  
  const isReadOnly = mode === "view";
  // Global admin: Service = "*" and service_id = "all" (null)
  // Module-specific admin: has specific service assigned
  const isGlobalAdmin = formData.Service === "*" || formData.service_id === "all";

  // Get unique roles from the fetched data
  const uniqueRoles = useMemo(() => {
    if (!usersRoles) return [];
    const rolesSet = new Set(usersRoles.map(r => r.roles));
    const excludedRoles = ['Super Admin'];
    return Array.from(rolesSet)
      .filter(role => !excludedRoles.includes(role))
      .sort();
  }, [usersRoles]);


  useEffect(() => {
    if (user && (mode === "edit" || mode === "view")) {
      let serviceIdValue = "";
      if (user.Service === "*" || user.service_id === "*") {
        serviceIdValue = "all";
      } else if (user.service_id) {
        serviceIdValue = user.service_id;
      }

      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        module: "e-ticketing",
        roles: user.roles || user.role || "",
        Service: user.Service === "*" ? "*" : user.Service || "",
        service_id: serviceIdValue,
        city_id: user.city_id || "",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        module: "e-ticketing",
        roles: "",
        Service: "",
        service_id: "",
        city_id: "",
      });
    }
    setValidationErrors({});
  }, [user, mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Handle service selection
      if (field === "service_id") {
        if (value === "all") {
          newData.Service = "*";
          newData.service_id = "all"; // Keep as "all" for form state, convert to null on submit
        } else if (value) {
          const selectedService = services?.find(s => Number(s.id) === Number(value));
          if (selectedService) {
            newData.Service = selectedService.service_name;
            newData.service_id = selectedService.id;
          }
        } else {
          newData.Service = "";
          newData.service_id = "";
        }
      }

      // Capitalize role on selection
      if (field === "roles") {
        newData.roles = value
          ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
          : "";
        
        // Admin role clears city_id (admins can be global or module-specific)
        if (value && value.toLowerCase() === "admin") {
          newData.city_id = "";
        }
      }

      return newData;
    });

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (mode === "create" && !formData.password.trim()) {
      errors.password = "Password is required";
    }

    if (mode === "create" && formData.password.trim() && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.roles.trim()) {
      errors.roles = "Role is required";
    }

    if (!formData.service_id) {
      errors.service_id = "Service is required";
    }

    // City is required only if role is not Admin
    if (formData.roles.toLowerCase() !== "admin" && !formData.city_id) {
      errors.city_id = "City is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === "view") {
      onCancel();
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      // Convert service_id: "all" or "*" -> null, otherwise ensure it's an integer
      let serviceId;
      if (formData.service_id === "all" || formData.service_id === "*") {
        serviceId = null;
      } else if (formData.service_id) {
        serviceId = Number(formData.service_id);
      } else {
        serviceId = null;
      }

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        roles: formData.roles.trim(),
        module: isGlobalAdmin ? "*" : "e-ticketing",
        Service: formData.Service,
        service_id: serviceId,
      };

      // Only include city_id if role is not Admin
      if (formData.roles.toLowerCase() === "admin") {
        payload.city_id = null;
      } else {
        payload.city_id = formData.city_id;
      }

      if (mode === "create" || (mode === "edit" && formData.password.trim())) {
        payload.password = formData.password;
      }

      if (mode === "create") {
        const success = await createCloudUser(payload);
        if (success) {
          onSave && onSave(payload);
        }
      } else if (mode === "edit" && (user?.id || user?._id)) {
        const userId = user.id || user._id;
        const success = await updateCloudUser(userId, payload);
        if (success) {
          onSave && onSave(payload);
        }
      }
    } catch (err) {
      console.error("E-ticketing user mutation error:", err);
    }
  };

  const getFormTitle = () => {
    switch (mode) {
      case "create":
        return "Add New E-Ticketing User";
      case "edit":
        return "Edit E-Ticketing User";
      case "view":
        return "View E-Ticketing User Details";
      default:
        return "E-Ticketing User";
    }
  };


  return (
    <Card className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={onCancel}>
          ← Back
        </Button>
        <h4 className="m-0">{getFormTitle()}</h4>
      </div>

      {errorUser && (
        <Alert variant="danger" dismissible>
          {errorUser}
        </Alert>
      )}

      {loadingServices && (
        <Alert variant="info">Loading services...</Alert>
      )}

      {errorServices && (
        <Alert variant="warning" dismissible>
          {errorServices}
        </Alert>
      )}

      {errorCities && (
        <Alert variant="warning" dismissible>
          Failed to load cities
        </Alert>
      )}

      {errorUsersRoles && (
        <Alert variant="warning" dismissible>
          Failed to load roles
        </Alert>
      )}

      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Name {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                isInvalid={!!validationErrors.name}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Email {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                isInvalid={!!validationErrors.email}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Password{" "}
                {!isReadOnly && mode === "create" && (
                  <span className="text-danger">*</span>
                )}
                {mode === "edit" && (
                  <small className="text-muted"> (Leave blank to keep current)</small>
                )}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder={mode === "edit" ? "Enter new password" : "Enter password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  isInvalid={!!validationErrors.password}
                  disabled={isReadOnly}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isReadOnly}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.password}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Role {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Select
                value={formData.roles}
                onChange={(e) => handleInputChange("roles", e.target.value)}
                isInvalid={!!validationErrors.roles}
                disabled={isReadOnly || loadingUsersRoles}
              >
                <option value="">Select Role</option>
                {uniqueRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.roles}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Module</Form.Label>
              <Form.Control type="text" value="E-Ticketing" disabled />
              <Form.Text className="text-muted">
                This form is for E-Ticketing module users only
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Service {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Select
                value={formData.service_id}
                onChange={(e) => handleInputChange("service_id", e.target.value)}
                isInvalid={!!validationErrors.service_id}
                disabled={isReadOnly || loadingServices}
              >
                <option value="">Select Service</option>
                {services?.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.service_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.service_id}
              </Form.Control.Feedback>
              {isGlobalAdmin && (
                <Form.Text className="text-muted">
                  Global admin has access to all modules and services
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                City {!isReadOnly && formData.roles.toLowerCase() !== "admin" && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Select
                value={formData.city_id}
                onChange={(e) => handleInputChange("city_id", e.target.value)}
                isInvalid={!!validationErrors.city_id}
                disabled={isReadOnly || loadingCities || formData.roles.toLowerCase() === "admin"}
              >
                <option value="">Select City</option>
                {cities?.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.city_id}
              </Form.Control.Feedback>
              {formData.roles.toLowerCase() === "admin" && (
                <Form.Text className="text-muted">
                  City selection is not required for Admin role
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>

        {mode === "view" && user && (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Created Date</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "-"
                  }
                  disabled
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Service</Form.Label>
                <Form.Control
                  type="text"
                  value={user.Service === "*" ? "All Services" : user.Service || "-"}
                  disabled
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="secondary" onClick={onCancel} disabled={loadingUser}>
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode !== "view" && (
            <Button variant="primary" onClick={handleSubmit} disabled={loadingUser}>
              {loadingUser ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {mode === "create" ? "Adding..." : "Saving..."}
                </>
              ) : mode === "create" ? (
                "Add User"
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default ETicketingUserForm;
